import React, { useState, useEffect, useRef, useCallback } from 'react';
import { clsx } from 'clsx';
import { autocompletePlaces, getPlaceDetails } from '../../services/googleGeocoding';
import type { GooglePlacePrediction } from '../../services/googleGeocoding';

export interface AddressComponents {
  street: string;
  zip: string;
  locality: string;
  country: string;
  region: string;
}

export interface LocationSelectData {
  name: string;
  address: string;
  coordinates: { lat: number; lng: number };
  addressComponents: AddressComponents;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (location: LocationSelectData) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  placeholder = 'Search for an address...',
  className,
  disabled = false
}) => {
  const [suggestions, setSuggestions] = useState<GooglePlacePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced search
  const handleSearch = useCallback(async (query: string) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const results = await autocompletePlaces(query);
      setSuggestions(results);
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle input change with debouncing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setSelectedIndex(-1);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    if (newValue.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        handleSearch(newValue);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle suggestion selection — fetch place details for coordinates + address components
  const handleSelectSuggestion = useCallback(async (suggestion: GooglePlacePrediction) => {
    onChange(suggestion.fullText);
    setSuggestions([]);
    setShowSuggestions(false);

    const details = await getPlaceDetails(suggestion.placeId);
    if (!details) return;

    onSelect({
      name: suggestion.primaryText,
      address: details.formattedAddress,
      coordinates: { lat: details.lat, lng: details.lng },
      addressComponents: {
        street: details.street,
        zip: details.zip,
        locality: details.locality,
        country: details.countryCode,
        region: details.region,
      },
    });
  }, [onChange, onSelect]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          void handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className={clsx(
            'input pr-10',
            className
          )}
          disabled={disabled}
          autoComplete="off"
        />
        
        {/* Loading indicator */}
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        
        {/* Location icon */}
        {!isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            📍
          </div>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.placeId}
              type="button"
              onClick={() => void handleSelectSuggestion(suggestion)}
              className={clsx(
                'w-full text-left px-4 py-3 transition-colors',
                'hover:bg-gray-50 focus:bg-gray-50 focus:outline-none',
                { 'bg-primary/5': index === selectedIndex }
              )}
            >
              <div className="flex items-start gap-2">
                <span className="text-gray-400 mt-0.5">📍</span>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{suggestion.primaryText}</div>
                  {suggestion.secondaryText && (
                    <div className="text-sm text-gray-500">{suggestion.secondaryText}</div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {showSuggestions && !isLoading && value.length >= 3 && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg p-4 text-center text-sm text-gray-500">
          No locations found for "{value}"
        </div>
      )}
    </div>
  );
};
